import { useState } from "react";
import { Check, ShieldCheck, Star, X } from "lucide-react";
import { backendConfigured, fetchReviewQueue, moderateBusinessReview } from "../lib/api";

export default function AdminReviews() {
  const [key, setKey] = useState("");
  const [reviews, setReviews] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async event => {
    event?.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      setReviews(await fetchReviewQueue(key));
    } catch (error) {
      setReviews([]);
      setMessage(error.response?.data?.detail || "Could not load the moderation queue.");
    } finally {
      setLoading(false);
    }
  };

  const moderate = async (id, status) => {
    try {
      await moderateBusinessReview(key, id, status);
      setReviews(items => items.filter(item => item.id !== id));
      setMessage(`Review ${status}.`);
    } catch (error) {
      setMessage(error.response?.data?.detail || "The moderation action failed.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-left text-slate-900">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center gap-3 text-emerald-700"><ShieldCheck /><span className="text-xs font-bold uppercase tracking-[.16em]">Private moderation</span></div>
        <h1 className="mt-5 font-display text-4xl font-bold md:text-6xl">Business review queue</h1>
        <p className="mt-4 max-w-2xl text-slate-600">Website submissions remain private until approved here. The key stays only in this page’s memory and is not saved in browser storage.</p>

        <form onSubmit={load} className="mt-8 flex max-w-xl gap-2">
          <input type="password" value={key} onChange={event => setKey(event.target.value)} required placeholder="Review admin key" autoComplete="off" className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-4 py-3" />
          <button disabled={!backendConfigured || loading} className="rounded-lg bg-slate-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{loading ? "Loading…" : "Open queue"}</button>
        </form>
        {!backendConfigured && <p className="mt-3 text-sm text-amber-700">The backend URL is not configured in this build.</p>}
        {message && <p className="mt-4 text-sm text-slate-600" role="status">{message}</p>}

        <div className="mt-10 grid gap-4">
          {reviews.map(review => (
            <article key={review.id} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><strong>{review.author_name}</strong><span className="ml-2 text-sm text-slate-500">{review.business_slug}</span></div>
                <div className="flex text-amber-500" aria-label={`${review.rating} out of 5 stars`}>{Array.from({ length: review.rating }, (_, index) => <Star key={index} size={15} fill="currentColor" />)}</div>
              </div>
              <p className="mt-4 leading-relaxed text-slate-700">{review.comment}</p>
              {review.email && <p className="mt-3 text-xs text-slate-500">Private contact: {review.email}</p>}
              <div className="mt-5 flex gap-2">
                <button type="button" onClick={() => moderate(review.id, "approved")} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-bold text-white"><Check size={16} /> Approve</button>
                <button type="button" onClick={() => moderate(review.id, "rejected")} className="inline-flex items-center gap-2 rounded-lg border border-rose-200 px-4 py-2 text-sm font-bold text-rose-700"><X size={16} /> Reject</button>
              </div>
            </article>
          ))}
          {!loading && key && reviews.length === 0 && <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500">No pending reviews.</p>}
        </div>
      </div>
    </div>
  );
}
